import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "@aws-northstar/ui/components";
import { Button, StatusIndicator } from "@cloudscape-design/components";

import LoadingSpinner from "../../components/LoadingSpinner";
import { GcpCert } from "../../types/gcpCert";
import useGcpCerts from "../../hooks/query/useGcpCerts";

const GcpCerts: FC = () => {
  const navigate = useNavigate();
  const { data: gcpCerts, isLoading } = useGcpCerts();

  if (isLoading || !gcpCerts) return <LoadingSpinner />;

  // ----- Helper Functions -----
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getFullYear()}`;
  };

  const renderCertificateRequest = (cert: GcpCert) => (
    <div>
      {(cert.details.dnsPrefixes || []).map((prefix: string, idx: number) => (
        <div key={idx}>{`${prefix}.${cert.details.dnsDomainName}`}</div>
      ))}
    </div>
  );

  const renderStatusIndicator = (status: string) => {
    const statusMap: Record<string, { type: string; label: string; color?: string }> = {
      PENDING_APPROVAL: { type: "in-progress", label: "Pending Approval", color: "blue" },
      APPROVED: { type: "success", label: "Approved" },
      REJECTED: { type: "error", label: "Rejected" },
    };

    const s = statusMap[status];
    if (!s) return null;

    return (
      <StatusIndicator type={s.type as any}>
        {s.label}
      </StatusIndicator>
    );
  };

  // ----- Column Definitions -----
  const columnDefinitions = [
    {
      id: "gcpCertID",
      width: 200,
      header: "ID",
      cell: (data: GcpCert) => (
        <Button variant="link" onClick={() => navigate(data.gcpCertID)}>
          {data.gcpCertID}
        </Button>
      ),
    },
    { id: "application", width: 150, header: "Application", cell: (data: GcpCert) => data.details.applicationName || data.details.application || data.details.applicationAlias || "" },
    { id: "platform", width: 150, header: "Platform", cell: (data: GcpCert) => data.details.platformName || "" },
    { id: "certificateRequest", width: 200, header: "Certificate Request", cell: renderCertificateRequest },
    { id: "requestor", width: 150, header: "Requested By", cell: (data: GcpCert) => data.requestorID || "" },
    { id: "time", width: 120, header: "Created Time", cell: (data: GcpCert) => formatDate(data.creationTime) },
    { id: "status", width: 150, header: "Approval Status", cell: (data: GcpCert) => renderStatusIndicator(data.status) },
  ];

  return (
    <Table
      header="GCP Certificates"
      columnDefinitions={columnDefinitions}
      items={gcpCerts}
      disableRowSelect
      sortingDisabled
      loading={isLoading}
    />
  );
};

export default GcpCerts;
